import { Emittable } from '@/data/productlist/generic.ts';
import { Currency, VoidPriceManager, PriceAttributes, PriceManager, PriceType } from '@/helpers/pricemanager';

import { roundUnit } from '@/data/api/products/helpers';

import _ from 'lodash';
import { mapState } from 'pinia';
import { useCartStore } from '@/stores/cart';
import { useAccountStore } from '@/stores/account';

interface VM {
  payer?: {
    priceListTable?: string;
  };
  c_price_visibility: PriceType;
  p_c_address_id?: string;
  p_c_customer_number?: string;
  p_c_prices_loading: boolean;
  c_price_managers: Record<string, PriceManager>;
  forceRecompute: () => void;
}

export class PriceManagerEmittable extends Emittable {
  cache: Record<string, any>;
  usePayerRegion: boolean;

  constructor({
    usePayerRegion = false,
    priceVisibility = PriceType.None,
    currency = Currency.SEK,
  }: {
    usePayerRegion?: boolean;
    priceVisibility?: PriceType;
    currency?: Currency;
  }) {
    super();
    const self = this;
    this.cache = {};
    this.usePayerRegion = usePayerRegion;
    this.mixin = {
      data() {
        return {
          c_price_managers: {} as Record<string, PriceManager>,
          p_c_prices_loading: false,
          c_price_visibility: priceVisibility ?? PriceType.None,
          c_price_currency: currency ?? Currency.SEK,
        };
      },
      computed: {
        ...mapState(useCartStore, ['default_price_visibility']),
        ...mapState(useAccountStore, ['isBendersEmployee']),
        c_activePrice: {
          set(val: PriceType) {
            // self.cache = {}; // Clear cache, to allow for recalculation
            (this as any as VM).c_price_visibility = [PriceType.Net, PriceType.Sales, PriceType.None].includes(val)
              ? val
              : PriceType.Sales;
          },
          get() {
            if ((this as any).isBendersEmployee && !(this as any).p_c_customer_number) return PriceType.Sales;
            return (this as any).c_price_visibility;
          },
        },
      },
      watch: {
        p_c_rows: {
          deep: true,
          handler(rows) {
            self._getPriceCollections.call(self, this, rows);
          },
        },
        payer: {
          deep: true,
          handler(payer) {
            if (self.usePayerRegion && payer?.priceListTable) {
              self._getPriceCollections.call(self, this, (this as any).p_c_rows);
            }
          },
        },
        p_c_customer_number(newValue, oldValue) {
          if (!newValue || newValue === oldValue) return;
          (this as any).c_price_managers = {}; // reset prices
          self._getPriceCollections.call(self, this, this.p_c_rows);
        },
        p_c_address_id(newValue, oldValue) {
          if (newValue === oldValue) return;
          (this as any).c_price_managers = {}; // reset prices
          self._getPriceCollections.call(self, this, this.p_c_rows);
        },
        default_price_visibility: {
          immediate: true,
          handler(visibility: { netPrice: boolean; salesPrice: boolean }) {
            if (
              (this as any).c_price_visibility === PriceType.None &&
              (visibility?.netPrice || visibility?.salesPrice)
            ) {
              (this as any).c_price_visibility = visibility?.netPrice ? PriceType.Net : PriceType.Sales;
            }
          },
        },
      },
    };
  }

  init() {
    this.cache = {};

    // this.on('price:currency', this.setCurrency.bind(this));
  }

  itemHasChanged(key, item) {
    if (!(key in this.cache)) {
      this.cache[key] = item;
      return true;
    }

    if (!_.isEqual(this.cache[key], item)) {
      this.cache[key] = item;
      return true;
    }
    return false;
  }

  calcAmount(row) {
    return roundUnit(
      row.amount * (row?.article?.unit?.[row.unit || 'st'] || 0) || row.amount,
      row.article.numberOfDecimalPlaces,
      false
    );
  }

  _forceRecompute = _.debounce(function (vm: VM) {
    console.log('PriceManagerEmittable: force recompute');
    vm.forceRecompute();
  }, 100);
  _getPriceCollections = _.debounce(this.getPriceCollections, 100);
  async getPriceCollections(vm: VM, rows) {
    if (!rows) return rows;

    let payerRegion;
    if (this.usePayerRegion) {
      payerRegion = vm.payer?.priceListTable;
      if (!payerRegion) return;
    }

    // Check data against cache
    const data = rows.reduce((acc, row) => {
      if (row.article && row.article.ArtNr) {
        const item = {
          id: row.id,
          artnr: row.article.ArtNr,
          amount: row?.amount,
          unit: row.unit,
          warehouse: row.warehouse,
          region: payerRegion,
          addon: row?._meta?.addon || false,
          salesUnit: row?.unitDetails?.salesUnit ?? row?.salesUnit,
        };
        if (!this.itemHasChanged(item.id, item) && !!vm.c_price_managers[row.id]) {
          return acc;
        }
        if (this.store('debug', 'debugMode')) console.log('Item has changed', _.cloneDeep(item));
        acc.push(item);
      }
      return acc;
    }, []);

    await this.store('cart', 'getInfo')?.();

    // Get price
    const promises = [] as Promise<any>[];
    for (let item of data) {
      if (this.store('debug', 'debugMode')) console.debug('Fetching price collection for item', item);
      // vm.c_price_managers[item.id]?.destroy(); // Clean up old price manager to avoid memory leaks
      const parsedQuantity = Number(item.amount);
      const attributes = {
        artnr: item.artnr,
        address: vm.p_c_address_id,
        customer: vm.p_c_customer_number ?? '',
        warehouse: item.warehouse,
        region: this.usePayerRegion ? payerRegion : undefined,
        quantity: isNaN(parsedQuantity) ? 0 : parsedQuantity,
        unit: item.unit,
        salespriceUnit: item.salesUnit,
      } as PriceAttributes;

      if (attributes.quantity != 0) {
        const existing = vm.c_price_managers[item.id];
        if (existing && !existing.isVoid) {
          if (this.store('debug', 'debugMode')) console.debug('Attempted to update pricemanager in productlist');
          existing.update_attributes(attributes);
        } else {
          if (this.store('debug', 'debugMode')) console.debug('Creating new Pricemanager in productlist');
          vm.c_price_managers[item.id] = new PriceManager(attributes);
        }
      } else if (vm.c_price_managers[item.id]) {
        vm.c_price_managers[item.id] = VoidPriceManager;
      }

      // Ensure that the we modify rows after the prices have been fetched
      vm.p_c_prices_loading = true;
      promises.push(vm.c_price_managers[item.id].awaitFetching());
    }

    if (promises.length > 0) {
      await Promise.all(promises);
      vm.p_c_prices_loading = false;
      this._forceRecompute(vm);
    }
  }

  modify(vm: VM, rows) {
    const modified = rows.map((row) => {
      if (row?.id) {
        // Note: This might break the 'reset' of prices
        // Note2: Swapped so row.price_manager is preferred, not sure what issues that can cause but resolves issues with price for M3Orders in step 1
        vm.c_price_managers[row.id] = row.price_manager || vm.c_price_managers[row.id];
        row.price_manager = vm.c_price_managers[row.id];
      }
      return row;
    });

    return modified;
  }
}
